import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Paper,
  CircularProgress,
  Stack,
  Grid,
  alpha,
  Chip,
  Snackbar,
  Alert,
  MenuItem,
  Divider,
  Link,
  Tooltip,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  ArrowBack as BackIcon,
  Business as BusinessIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  ZoomIn as ZoomIcon,
  Image as ImageIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  createCustomer,
  getCustomerById,
  updateCustomer,
} from "../../../Services/Services";

// ─── Constants ───────────────────────────────────────────────────
import { brandColor as brand } from "../../../theme";

// ─── Shared style objects ─────────────────────────────────────────
const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "4px",
    backgroundColor: "#f8fafc",
    transition: "all 0.2s ease",
    "& fieldset": { borderColor: "#e5e7eb" },
    "&:hover fieldset": { borderColor: "#c7d2fe" },
    "&.Mui-focused fieldset": { borderColor: brand, borderWidth: 1.5 },
  },
  "& .MuiInputLabel-root": { fontSize: "0.78rem", color: "#6b7280" },
  "& .MuiInputLabel-root.Mui-focused": { color: brand },
  "& .MuiFormHelperText-root": { fontSize: "0.72rem", marginLeft: "4px" },
};

// ─── Section Title ────────────────────────────────────────────────
const SectionTitle = ({ title, subtitle }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
    <Box sx={{ width: 3, height: 18, bgcolor: brand, borderRadius: 4 }} />
    <Typography sx={{ fontWeight: 700, color: "#111827", fontSize: "0.85rem" }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography sx={{ fontSize: "0.72rem", color: "#9ca3af", ml: 0.5 }}>
        {subtitle}
      </Typography>
    )}
  </Stack>
);

// ─── Main Component ───────────────────────────────────────────────
const OurCustomerForm = ({ editId = null, onSuccess, onCancel }) => {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const isEdit = Boolean(editId);

  // ─── State ────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    name: "",
    display_order: 0,
    is_active: true,
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [existingLogoUrl, setExistingLogoUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [nameError, setNameError] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // ─── Effects ─────────────────────────────────────────────────
  useEffect(() => {
    if (isEdit) fetchCustomer();
  }, [editId]);

  // ─── Fetch existing customer ──────────────────────────────────
  const fetchCustomer = async () => {
    setFetching(true);
    try {
      const data = await getCustomerById(editId);
      if (data && data.id) {
        setFormData({
          name: data.name || "",
          display_order: data.display_order || 0,
          is_active: data.is_active ?? true,
        });
        if (data.logo) {
          setLogoPreview(data.logo);
          setExistingLogoUrl(data.logo);
        }
      }
    } catch {
      setSnackbar({
        open: true,
        message: "Failed to load customer data.",
        severity: "error",
      });
    }
    setFetching(false);
  };

  // ─── Handlers ────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "name") {
      setNameError(value.trim() ? "" : "Customer name is required");
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setSnackbar({
        open: true,
        message: "File size must be under 5MB.",
        severity: "error",
      });
      return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setSnackbar({
      open: true,
      message: "Logo uploaded successfully!",
      severity: "success",
    });
  };

  const removeLogo = () => {
    if (logoFile && existingLogoUrl) {
      setLogoFile(null);
      setLogoPreview(existingLogoUrl);
      if (fileRef.current) fileRef.current.value = "";
      setSnackbar({
        open: true,
        message: "Reverted to existing logo",
        severity: "info",
      });
      return;
    }
    setLogoFile(null);
    setLogoPreview(null);
    setExistingLogoUrl(null);
    if (fileRef.current) fileRef.current.value = "";
    setSnackbar({
      open: true,
      message: "Logo removed",
      severity: "info",
    });
  };

  const handleClearAll = () => {
    setFormData({ name: "", display_order: 0, is_active: true });
    setLogoFile(null);
    setLogoPreview(null);
    setExistingLogoUrl(null);
    setNameError("");
    if (fileRef.current) fileRef.current.value = "";
    setSnackbar({
      open: true,
      message: "All fields cleared!",
      severity: "info",
    });
  };

  const handleBack = () => {
    if (onCancel) onCancel();
    else navigate("/dashboard/customer-list");
  };

  // ─── Submit ───────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setNameError("Customer name is required");
      setSnackbar({
        open: true,
        message: "Customer name is required.",
        severity: "error",
      });
      return;
    }

    setLoading(true);
    try {
      const payload = new FormData();
      payload.append("name", formData.name.trim());
      payload.append("display_order", formData.display_order);
      payload.append("is_active", formData.is_active);
      if (logoFile) payload.append("logo", logoFile);

      const result = isEdit
        ? await updateCustomer(editId, payload)
        : await createCustomer(payload);

      const data = result?.data ?? result;
      const explicitFailure =
        data?.success === false || data?.error || data?.status === "error";

      if (explicitFailure) {
        throw new Error(data?.message || data?.error || "Something went wrong");
      }

      setSnackbar({
        open: true,
        message: `Customer ${isEdit ? "updated" : "created"} successfully!`,
        severity: "success",
      });
      if (onSuccess) onSuccess(data);
      setTimeout(() => navigate("/dashboard/customer-list"), 1200);
    } catch (error) {
      let msg = "Failed to save customer.";
      if (error?.response?.data?.message) msg = error.response.data.message;
      else if (error?.message) msg = error.message;
      setSnackbar({ open: true, message: msg, severity: "error" });
    }
    setLoading(false);
  };

  // ─── Derived helpers ──────────────────────────────────────────
  const isNewUpload = !!logoFile;
  const isExistingLogo = !logoFile && !!logoPreview && !!existingLogoUrl;
  const hasLogo = !!logoPreview;

  // ─── Loading state ────────────────────────────────────────────
  if (fetching) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress sx={{ color: brand }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{ px: 0, pt: 0, mt: -2, maxWidth: 1200, mx: "auto", width: "100%" }}
    >
      <Paper
        sx={{
          borderRadius: "4px",
          border: "1px solid #e5e7eb",
          boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
          overflow: "hidden",
          p: 0,
        }}
      >
        {/* ── Header ── */}
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            backgroundColor: "#f8fafc",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "4px",
                  bgcolor: alpha(brand, 0.1),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <BusinessIcon sx={{ color: brand, fontSize: 18 }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#111827",
                    fontSize: "1rem",
                    lineHeight: 1.2,
                    textAlign: "left",
                  }}
                >
                  {isEdit ? "Edit Customer" : "Add New Customer"}
                </Typography>
                <Typography sx={{ fontSize: "0.7rem", color: "#6b7280", mt: 0.2 }}>
                  {isEdit
                    ? `Editing Customer ID: ${editId}`
                    : "Fill in the details below to add a customer"}
                </Typography>
              </Box>
              {isEdit && (
                <Chip
                  label={`ID: ${editId}`}
                  size="small"
                  sx={{
                    borderRadius: "4px",
                    bgcolor: alpha(brand, 0.1),
                    color: brand,
                    fontSize: "0.65rem",
                    fontWeight: 600,
                    height: 20,
                  }}
                />
              )}
            </Stack>

            <Button
              variant="outlined"
              startIcon={<BackIcon sx={{ fontSize: 16 }} />}
              onClick={handleBack}
              size="small"
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontSize: "0.75rem",
                px: 2,
                py: 0.75,
                borderColor: "#d1d5db",
                color: "#6b7280",
                boxShadow: "none",
                "&:hover": {
                  borderColor: brand,
                  color: brand,
                  bgcolor: alpha(brand, 0.04),
                  boxShadow: "none",
                },
              }}
            >
              Back to List
            </Button>
          </Box>
        </Box>

        {/* ── Form Body ── */}
        <Box sx={{ p: 2.5 }}>
          <Grid container spacing={2.5}>

            {/* ── Left Column: always md={8} ── */}
            <Grid item xs={12} md={8}>

              {/* Section: Customer Details */}
              <SectionTitle title="Customer Details" />

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Customer / Company Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onBlur={(e) =>
                      setNameError(
                        e.target.value.trim() ? "" : "Customer name is required"
                      )
                    }
                    fullWidth
                    required
                    size="small"
                    sx={textFieldSx}
                    placeholder="Enter customer or company name"
                    error={!!nameError}
                    helperText={nameError}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Display Order"
                    name="display_order"
                    type="number"
                    value={formData.display_order}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    sx={textFieldSx}
                    helperText="Lower number appears first"
                  />
                </Grid>
              </Grid>

              {/* ── Divider ── */}
              <Divider sx={{ my: 2.5, borderColor: "#e5e7eb" }} />

              {/* Section: Logo & Status */}
              <SectionTitle
                title="Company Logo & Status"
                subtitle="(Max 5MB · PNG, JPG, WEBP accepted)"
              />

              <Grid container spacing={2}>

                {/* Upload field */}
                <Grid item xs={12} sm={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: "4px",
                      border: hasLogo
                        ? "1.5px solid #10b981"
                        : "1.5px dashed #d1d5db",
                      bgcolor: hasLogo
                        ? alpha("#10b981", 0.04)
                        : "#f8fafc",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {!hasLogo ? (
                      <Button
                        variant="text"
                        component="label"
                        fullWidth
                        startIcon={<UploadIcon sx={{ fontSize: 18 }} />}
                        sx={{
                          borderRadius: "4px",
                          textTransform: "none",
                          fontSize: "0.78rem",
                          color: brand,
                          py: "7.5px",
                          px: 1.5,
                          justifyContent: "flex-start",
                          bgcolor: "transparent",
                          boxShadow: "none",
                          "&:hover": {
                            bgcolor: alpha(brand, 0.06),
                            boxShadow: "none",
                          },
                        }}
                      >
                        Choose Logo
                        <input
                          type="file"
                          hidden
                          ref={fileRef}
                          accept="image/*"
                          onChange={handleFileChange}
                        />
                      </Button>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          px: 1,
                          py: "7.5px",
                        }}
                      >
                        <ImageIcon sx={{ color: brand, fontSize: 18 }} />
                        <Typography
                          variant="body2"
                          sx={{
                            flex: 1,
                            fontSize: "0.78rem",
                            color: "#374151",
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {isNewUpload
                            ? logoFile.name
                            : existingLogoUrl
                            ? existingLogoUrl.split("/").pop()
                            : "Saved logo"}
                        </Typography>

                        {isNewUpload && (
                          <Chip
                            label="New"
                            size="small"
                            sx={{
                              borderRadius: "4px",
                              bgcolor: alpha(brand, 0.1),
                              color: brand,
                              fontSize: "0.6rem",
                              fontWeight: 600,
                              height: 18,
                              px: 0.5,
                            }}
                          />
                        )}

                        {isExistingLogo && existingLogoUrl && (
                          <Tooltip title="View logo">
                            <IconButton
                              size="small"
                              component={Link}
                              href={existingLogoUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                color: brand,
                                borderRadius: "4px",
                                p: 0.4,
                                "&:hover": { bgcolor: alpha(brand, 0.08) },
                              }}
                            >
                              <ZoomIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        )}

                        {isNewUpload && (
                          <Tooltip title="Preview">
                            <IconButton
                              size="small"
                              onClick={() => setLightboxOpen(true)}
                              sx={{
                                color: brand,
                                borderRadius: "4px",
                                p: 0.4,
                                "&:hover": { bgcolor: alpha(brand, 0.08) },
                              }}
                            >
                              <ZoomIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        )}

                        <Tooltip title="Replace">
                          <IconButton
                            size="small"
                            component="label"
                            sx={{
                              color: "#6b7280",
                              borderRadius: "4px",
                              p: 0.4,
                              "&:hover": {
                                color: brand,
                                bgcolor: alpha(brand, 0.08),
                              },
                            }}
                          >
                            <UploadIcon sx={{ fontSize: 15 }} />
                            <input
                              type="file"
                              hidden
                              accept="image/*"
                              onChange={handleFileChange}
                            />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Remove">
                          <IconButton
                            size="small"
                            onClick={removeLogo}
                            sx={{
                              color: "#dc2626",
                              borderRadius: "4px",
                              p: 0.4,
                              "&:hover": { bgcolor: alpha("#dc2626", 0.08) },
                            }}
                          >
                            <CloseIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Paper>
                </Grid>

                {/* Status field */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Status"
                    name="is_active"
                    value={formData.is_active ? "active" : "inactive"}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        is_active: e.target.value === "active",
                      }))
                    }
                    fullWidth
                    size="small"
                    sx={textFieldSx}
                    helperText="Visibility on website"
                  >
                    <MenuItem value="active">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <ActiveIcon sx={{ fontSize: 16, color: "#10b981" }} />
                        <Typography sx={{ fontSize: "0.82rem" }}>Active</Typography>
                      </Stack>
                    </MenuItem>
                    <MenuItem value="inactive">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <InactiveIcon sx={{ fontSize: 16, color: "#ef4444" }} />
                        <Typography sx={{ fontSize: "0.82rem" }}>Inactive</Typography>
                      </Stack>
                    </MenuItem>
                  </TextField>
                </Grid>

              </Grid>
            </Grid>

            {/* ── Right Column: Preview Panel – always visible ── */}
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: "4px",
                  border: "1px solid #e5e7eb",
                  overflow: "hidden",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Preview header bar */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 1.5,
                    py: 0.6,
                    bgcolor: "#f8fafc",
                    borderBottom: "1px solid #e5e7eb",
                    flexShrink: 0,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.8}>
                    <ImageIcon sx={{ fontSize: 14, color: "#9ca3af" }} />
                    <Typography
                      sx={{
                        fontSize: "0.68rem",
                        color: "#6b7280",
                        fontWeight: 500,
                      }}
                    >
                      Image Preview
                    </Typography>
                    
                  </Stack>

                  {hasLogo && (
                    <IconButton
                      size="small"
                      onClick={() => setLightboxOpen(true)}
                      sx={{
                        p: 0.3,
                        borderRadius: "4px",
                        color: "#6b7280",
                        "&:hover": { color: brand, bgcolor: alpha(brand, 0.08) },
                      }}
                    >
                      <ZoomIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>

                {/* Image area – placeholder or real image */}
                {!hasLogo ? (
                  /* ── Empty placeholder ── */
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      height: 150,
                      bgcolor: alpha(brand, 0.015),
                      // subtle checkerboard
                      backgroundImage:
                        "linear-gradient(45deg,#f3f4f6 25%,transparent 25%),linear-gradient(-45deg,#f3f4f6 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#f3f4f6 75%),linear-gradient(-45deg,transparent 75%,#f3f4f6 75%)",
                      backgroundSize: "16px 16px",
                      backgroundPosition: "0 0,0 8px,8px -8px,-8px 0px",
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        bgcolor: alpha(brand, 0.08),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <ImageIcon
                        sx={{ fontSize: 20, color: alpha(brand, 0.35) }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        color: "#9ca3af",
                        fontWeight: 500,
                      }}
                    >
                      No logo uploaded yet
                    </Typography>
                    <Typography
                      sx={{ fontSize: "0.65rem", color: "#c4c9d4" }}
                    >
                      Preview will appear here
                    </Typography>
                  </Box>
                ) : (
                  /* ── Real image ── */
                  <Box
                    onClick={() => setLightboxOpen(true)}
                    sx={{
                      height: 150,
                      cursor: "zoom-in",
                      overflow: "hidden",
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      component="img"
                      src={logoPreview}
                      alt="Logo preview"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                        transition: "transform 0.25s ease",
                        "&:hover": { transform: "scale(1.04)" },
                      }}
                    />
                  </Box>
                )}
              </Paper>
            </Grid>

          </Grid>

          {/* ── Action Buttons ── */}
          <Stack
            direction="row"
            spacing={1.5}
            justifyContent="flex-end"
            sx={{ mt: 3 }}
          >
            <Button
              startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
              onClick={handleClearAll}
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontSize: "0.75rem",
                px: 2.5,
                py: 0.9,
                bgcolor: alpha("#dc2626", 0.07),
                color: "#dc2626",
                boxShadow: "none",
                "&:hover": {
                  bgcolor: alpha("#dc2626", 0.14),
                  boxShadow: "none",
                },
              }}
            >
              Clear All
            </Button>

            <Button
              variant="contained"
              disabled={loading}
              startIcon={
                loading ? (
                  <CircularProgress size={16} sx={{ color: "#fff" }} />
                ) : (
                  <SaveIcon sx={{ fontSize: 16 }} />
                )
              }
              onClick={handleSubmit}
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontSize: "0.75rem",
                px: 2.5,
                py: 0.9,
                bgcolor: brand,
                boxShadow: "none",
                "&:hover": { bgcolor: "#4338ca", boxShadow: "none" },
                "&.Mui-disabled": {
                  bgcolor: alpha(brand, 0.5),
                  color: "#fff",
                },
              }}
            >
              {loading ? "Saving…" : isEdit ? "Update Customer" : "Add Customer"}
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* ── Lightbox ── */}
      {lightboxOpen && logoPreview && (
        <Box
          onClick={() => setLightboxOpen(false)}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            bgcolor: "rgba(0,0,0,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "zoom-out",
            backdropFilter: "blur(4px)",
          }}
        >
          <IconButton
            onClick={() => setLightboxOpen(false)}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              bgcolor: "rgba(255,255,255,0.1)",
              color: "#fff",
              borderRadius: "4px",
              "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
            }}
          >
            <CloseIcon />
          </IconButton>

          <Chip
            label={
              logoFile
                ? `${logoFile.name} — ${(logoFile.size / 1024).toFixed(1)} KB`
                : "Logo Preview"
            }
            size="small"
            sx={{
              position: "absolute",
              bottom: 20,
              bgcolor: "rgba(255,255,255,0.12)",
              color: "#fff",
              fontSize: "0.72rem",
              borderRadius: "4px",
              height: 24,
            }}
          />

          <Box
            component="img"
            src={logoPreview}
            alt="Full preview"
            onClick={(e) => e.stopPropagation()}
            sx={{
              maxWidth: "85vw",
              maxHeight: "85vh",
              objectFit: "contain",
              borderRadius: "4px",
              boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
              cursor: "default",
              bgcolor: "#fff",
              p: 2,
            }}
          />
        </Box>
      )}

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{
    top: "80px !important",
  }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{
            borderRadius: "4px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OurCustomerForm;